import React, { useState, useEffect } from 'react';
import {
    FaSearch,
    FaFilter,
    FaCheck,
    FaBan,
    FaTrash,
    FaPlus,
    FaTimes,
    FaUserCircle,
    FaCalendarAlt,
    FaCrown,
    FaUserShield,
    FaUser,
    FaPhone,
    FaVenusMars,
    FaKey
} from 'react-icons/fa';
import CustomDropdown from './CustomDropdown';
import api from '../../services/api';

const UserManagement = () => {
    const [users, setUsers] = useState([]);
    const [loading, setLoading] = useState(true);
    const [page, setPage] = useState(1);
    const [totalPages, setTotalPages] = useState(1);
    const [search, setSearch] = useState('');
    const [showAddModal, setShowAddModal] = useState(false);
    const [newUser, setNewUser] = useState({ name: '', email: '', password: '', mobile: '', role: 'user', gender: 'Male' });
    const [message, setMessage] = useState({ text: '', type: '', show: false });
    const [selectedUser, setSelectedUser] = useState(null);
    const [showDetailModal, setShowDetailModal] = useState(false);
    const [detailLoading, setDetailLoading] = useState(false);

    // New states for confirmation modal
    const [showConfirmModal, setShowConfirmModal] = useState(false);
    const [pendingAction, setPendingAction] = useState(null); // { userId, action, userName }

    useEffect(() => {
        fetchUsers();
    }, [page, search]);

    const showMessage = (text, type) => {
        setMessage({ text, type, show: true });
        setTimeout(() => {
            setMessage({ text: '', type: '', show: false });
        }, 3000);
    };

    const fetchUsers = async () => {
        setLoading(true);
        try {
            const res = await api.get(`/admin/users?page=${page}&limit=10&search=${search}`);
            setUsers(res.data.users);
            setTotalPages(res.data.totalPages);
        } catch (error) {
            console.error('Failed to fetch users', error);
            showMessage('Failed to fetch users', 'error');
        } finally {
            setLoading(false);
        }
    };

    const handleAddUser = async (e) => {
        e.preventDefault();
        try {
            await api.post('/admin/users', newUser);
            showMessage('User added successfully', 'success');
            setShowAddModal(false);
            setNewUser({ name: '', email: '', password: '', mobile: '', role: 'user', gender: 'Male' });
            fetchUsers();
        } catch (error) {
            showMessage(error.response?.data?.message || 'Failed to add user', 'error');
        }
    };

    const openConfirmModal = (userId, action, userName) => {
        setPendingAction({ userId, action, userName });
        setShowConfirmModal(true);
    };

    const executeAction = async () => {
        if (!pendingAction) return;

        const { userId, action, userName } = pendingAction;
        const actionText = {
            'verify': 'verify',
            'block': 'block',
            'unblock': 'unblock',
            'delete': 'delete'
        }[action];

        showMessage(`Processing ${actionText}...`, 'info');

        try {
            await api.post('/admin/users/action', { userId, action });
            showMessage(`User ${userName} ${actionText}ed successfully`, 'success');
            fetchUsers();
        } catch (error) {
            showMessage(`Failed to ${actionText} user`, 'error');
        } finally {
            setShowConfirmModal(false);
            setPendingAction(null);
        }
    };

    const getStatusColor = (status) => {
        switch (status) {
            case 'Active': return 'bg-blue-500 text-white';
            case 'Verified': return 'bg-blue-400 text-white';
            case 'Pending': return 'bg-blue-300 text-white';
            case 'Blocked': return 'bg-gray-400 text-white';
            default: return 'bg-gray-300 text-white';
        }
    };

    const getRoleIcon = (role) => {
        switch (role) {
            case 'admin': return <FaUserShield className="text-blue-500" />;
            case 'broker': return <FaCrown className="text-blue-400" />;
            default: return <FaUser className="text-blue-300" />;
        }
    };

    const getActionTitle = (action) => {
        switch (action) {
            case 'verify': return 'Verify User';
            case 'block': return 'Block User';
            case 'unblock': return 'Unblock User';
            case 'delete': return 'Delete User';
            default: return 'Confirm Action';
        }
    };

    const getActionMessage = (action, userName) => {
        switch (action) {
            case 'verify': return `Are you sure you want to verify ${userName}?`;
            case 'block': return `Are you sure you want to block ${userName}?`;
            case 'unblock': return `Are you sure you want to unblock ${userName}?`;
            case 'delete': return `Are you sure you want to permanently delete ${userName}? This action cannot be undone.`;
            default: return 'Are you sure?';
        }
    };
    const genderOptions = [
        { label: 'Male', value: 'Male' },
        { label: 'Female', value: 'Female' },
        { label: 'Other', value: 'Other' }
    ];

    const genderIcons = {
        Male: <FaVenusMars className="text-blue-400" />,
        Female: <FaVenusMars className="text-blue-500" />,
        Other: <FaVenusMars className="text-gray-500" />
    };

    const openUserDetails = async (userId) => {
        setDetailLoading(true);
        setShowDetailModal(true);

        try {
            const res = await api.get(`/admin/${userId}`);
            setSelectedUser(res.data);
        } catch (error) {
            console.error('Failed to fetch user details', error);
            showMessage('Failed to load user profile', 'error');
            setShowDetailModal(false);
        } finally {
            setDetailLoading(false);
        }
    };


    return (
        <div className="space-y-6">
            {/* Header Section */}
            <div className="bg-white rounded-2xl shadow-lg p-6 border border-gray-200">
                <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
                    <div>
                        <h1 className="text-2xl font-bold text-gray-800">User Management</h1>
                        <p className="text-gray-600 mt-1">Manage all users and their permissions</p>
                    </div>
                    <div className="flex items-center space-x-4">
                        <button
                            onClick={() => fetchUsers()}
                            className="flex items-center px-5 py-2.5 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-xl font-medium transition-all duration-200"
                        >
                            <FaFilter className="mr-2" /> Refresh
                        </button>
                        <button
                            onClick={() => setShowAddModal(true)}
                            className="flex items-center px-5 py-2.5 bg-blue-500 hover:bg-blue-600 text-white rounded-xl font-medium shadow-lg shadow-blue-500/30 transition-all duration-200"
                        >
                            <FaPlus className="mr-2" /> Add New User
                        </button>
                    </div>
                </div>

                {/* Search Bar */}
                <div className="mt-6">
                    <div className="relative">
                        <FaSearch className="absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-400 text-lg" />
                        <input
                            type="text"
                            placeholder="Search by name, email, or mobile..."
                            value={search}
                            onChange={(e) => setSearch(e.target.value)}
                            className="w-full pl-12 pr-4 py-3.5 bg-gray-50 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-gray-700"
                        />
                    </div>
                </div>
            </div>

            {/* Users Table */}
            <div className="bg-white rounded-2xl shadow-lg border border-gray-200 overflow-hidden">
                {loading ? (
                    <div className="p-16 text-center">
                        <div className="inline-block animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500"></div>
                        <p className="mt-4 text-gray-600">Loading users...</p>
                    </div>
                ) : (
                    <>
                        <div className="overflow-x-auto">
                            <table className="w-full">
                                <thead className="bg-gray-50 border-b border-gray-200">
                                    <tr>
                                        <th className="px-8 py-5 font-semibold text-gray-600 text-sm uppercase tracking-wider">User Profile</th>
                                        <th className="px-8 py-5 font-semibold text-gray-600 text-sm uppercase tracking-wider">Role</th>
                                        <th className="px-8 py-5 font-semibold text-gray-600 text-sm uppercase tracking-wider">Plan</th>
                                        <th className="px-8 py-5 font-semibold text-gray-600 text-sm uppercase tracking-wider">Joined Date</th>
                                        <th className="px-8 py-5 font-semibold text-gray-600 text-sm uppercase tracking-wider">Status</th>
                                        <th className="px-8 py-5 font-semibold text-gray-600 text-sm uppercase tracking-wider text-right">Actions</th>
                                    </tr>
                                </thead>
                                <tbody className="divide-y divide-gray-100">
                                    {users.length > 0 ? users.map((user) => (
                                        <tr
                                            key={user._id}
                                            className="hover:bg-gray-50/80 transition-colors duration-150 cursor-pointer"
                                            onClick={() => openUserDetails(user._id)}
                                        >
                                            <td className="px-8 py-5">
                                                <div className="flex items-center space-x-4">
                                                    <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-blue-400 to-blue-500 flex items-center justify-center text-white font-bold text-lg uppercase shadow-md">
                                                        {user.name?.[0]}
                                                    </div>
                                                    <div>
                                                        <div className="flex items-center space-x-2">
                                                            <p className="font-semibold text-gray-800">{user.name}</p>
                                                            {user.isVerified && (
                                                                <span className="px-2 py-1 bg-blue-100 text-blue-600 text-xs font-medium rounded-full flex items-center">
                                                                    <FaCheck className="mr-1" /> Verified
                                                                </span>
                                                            )}
                                                        </div>
                                                        <p className="text-sm text-gray-600 mt-1">{user.email}</p>
                                                        <p className="text-xs text-gray-500 mt-1 flex items-center">
                                                            <FaPhone className="mr-1" /> {user.mobile}
                                                        </p>
                                                    </div>
                                                </div>
                                            </td>
                                            <td className="px-8 py-5">
                                                <div className="flex items-center space-x-2">
                                                    {getRoleIcon(user.role)}
                                                    <span className={`px-4 py-2 rounded-lg text-sm font-medium uppercase ${user.role === 'admin' ? 'bg-blue-50 text-blue-600' :
                                                        user.role === 'broker' ? 'bg-blue-100 text-blue-700' :
                                                            'bg-gray-50 text-gray-700'
                                                        }`}>
                                                        {user.role}
                                                    </span>
                                                </div>
                                            </td>
                                            <td className="px-8 py-5">
                                                <div className="flex items-center space-x-2">
                                                    <FaCrown className={`${user.subscriptionPlan === 'Premium' ? 'text-blue-500' : 'text-gray-400'
                                                        }`} />
                                                    <span className="font-medium text-gray-700 capitalize">
                                                        {user.subscriptionPlan || 'Free'}
                                                    </span>
                                                </div>
                                            </td>
                                            <td className="px-8 py-5">
                                                <div className="flex items-center text-sm text-gray-600">
                                                    <FaCalendarAlt className="mr-2 text-gray-400" />
                                                    {new Date(user.createdAt).toLocaleDateString('en-US', {
                                                        day: 'numeric',
                                                        month: 'short',
                                                        year: 'numeric'
                                                    })}
                                                </div>
                                            </td>
                                            <td className="px-8 py-5">
                                                <span className={`px-3 py-1.5 rounded-full text-xs font-medium ${
                                                    user.status === 'inactive' ? 'bg-red-100 text-red-700' :
                                                    user.isBlocked ? 'bg-yellow-100 text-yellow-700' :
                                                    'bg-green-100 text-green-700'
                                                }`}>
                                                    {user.status === 'inactive' ? 'Deleted' : user.isBlocked ? 'Blocked' : 'Active'}
                                                </span>
                                            </td>
                                            <td className="px-8 py-5">
                                                <div className="flex justify-end space-x-2">
                                                    {!user.isVerified && (
                                                        <button
                                                            onClick={(e) => {
                                                                e.stopPropagation();
                                                                openConfirmModal(user._id, 'verify', user.name);
                                                            }}
                                                            className="p-3 bg-blue-50 hover:bg-blue-100 text-blue-600 rounded-xl transition-colors duration-200"
                                                            title="Verify User"
                                                        >
                                                            <FaCheck />
                                                        </button>
                                                    )}
                                                    {user.isBlocked ? (
                                                        <button
                                                            onClick={() => openConfirmModal(user._id, 'unblock', user.name)}
                                                            className="p-3 bg-gray-50 hover:bg-gray-100 text-gray-600 rounded-xl transition-colors duration-200"
                                                            title="Unblock User"
                                                        >
                                                            <FaBan />
                                                        </button>
                                                    ) : (
                                                        <button
                                                            onClick={(e) => {
                                                                e.stopPropagation();
                                                                openConfirmModal(
                                                                    user._id,
                                                                    user.isBlocked ? 'unblock' : 'block',
                                                                    user.name
                                                                );
                                                            }}
                                                            className="p-3 bg-gray-50 hover:bg-gray-100 text-gray-600 rounded-xl transition-colors duration-200"
                                                            title="Block User"
                                                        >
                                                            <FaBan />
                                                        </button>
                                                    )}
                                                    <button
                                                        onClick={(e) => {
                                                            e.stopPropagation();
                                                            openConfirmModal(user._id, 'delete', user.name);
                                                        }}
                                                        className="p-3 bg-gray-50 hover:bg-gray-100 text-gray-600 rounded-xl transition-colors duration-200"
                                                        title="Delete User"
                                                    >
                                                        <FaTrash />
                                                    </button>
                                                </div>
                                            </td>
                                        </tr>
                                    )) : (
                                        <tr>
                                            <td colSpan="5" className="text-center py-16">
                                                <div className="flex flex-col items-center">
                                                    <FaUserCircle className="text-6xl text-gray-300 mb-4" />
                                                    <p className="text-gray-600 text-lg font-medium">No users found</p>
                                                    <p className="text-gray-500 mt-1">Try adjusting your search criteria</p>
                                                </div>
                                            </td>
                                        </tr>
                                    )}
                                </tbody>
                            </table>
                        </div>

                        {/* Pagination */}
                        <div className="px-8 py-5 border-t border-gray-200 bg-gray-50">
                            <div className="flex flex-col md:flex-row justify-between items-center space-y-4 md:space-y-0">
                                <p className="text-gray-600">
                                    Showing page <span className="font-semibold text-gray-800">{page}</span> of <span className="font-semibold text-gray-800">{totalPages}</span>
                                </p>
                                <div className="flex space-x-2">
                                    <button
                                        onClick={() => setPage(page - 1)}
                                        disabled={page === 1}
                                        className="px-4 py-2.5 bg-white border border-gray-300 text-gray-700 rounded-xl hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors duration-200 font-medium"
                                    >
                                        Previous
                                    </button>
                                    <button
                                        onClick={() => setPage(page + 1)}
                                        disabled={page === totalPages}
                                        className="px-4 py-2.5 bg-blue-500 text-white rounded-xl hover:bg-blue-600 disabled:opacity-50 disabled:cursor-not-allowed transition-colors duration-200 font-medium"
                                    >
                                        Next
                                    </button>
                                </div>
                            </div>
                        </div>
                    </>
                )}
            </div>
            {/* User Detail Modal */}
            {showDetailModal && (
                <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4 overflow-y-auto">
                    <div className="bg-white rounded-2xl shadow-2xl w-full max-w-4xl max-h-[90vh] overflow-y-auto">
                        {/* Header */}
                        <div className="sticky top-0 bg-white border-b border-gray-200 px-8 py-6 rounded-t-2xl flex justify-between items-center">
                            <div className="flex items-center space-x-4">
                                <div className="w-20 h-20 rounded-2xl bg-gradient-to-br from-blue-400 to-blue-600 flex items-center justify-center text-white text-3xl font-bold shadow-lg">
                                    {selectedUser?.name?.[0]?.toUpperCase() || 'U'}
                                </div>
                                <div>
                                    <h2 className="text-2xl font-bold text-gray-800">{selectedUser?.name}</h2>
                                    <div className="flex items-center space-x-3 mt-1">
                                        <span className={`px-3 py-1 rounded-full text-xs font-medium ${selectedUser?.isVerified ? 'bg-blue-100 text-blue-700' : 'bg-gray-100 text-gray-600'
                                            }`}>
                                            {selectedUser?.isVerified ? 'Verified' : 'Not Verified'}
                                        </span>
                                        {selectedUser?.isBlocked && (
                                            <span className="px-3 py-1 bg-red-100 text-red-700 rounded-full text-xs font-medium">
                                                Blocked
                                            </span>
                                        )}
                                        <span className="px-3 py-1 bg-purple-100 text-purple-700 rounded-full text-xs font-medium capitalize">
                                            {selectedUser?.role}
                                        </span>
                                    </div>
                                </div>
                            </div>
                            <button
                                onClick={() => {
                                    setShowDetailModal(false);
                                    setSelectedUser(null);
                                }}
                                className="p-2.5 hover:bg-gray-100 rounded-xl transition-colors"
                            >
                                <FaTimes className="text-gray-500 text-xl" />
                            </button>
                        </div>

                        {detailLoading ? (
                            <div className="p-16 text-center">
                                <div className="inline-block animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500"></div>
                                <p className="mt-4 text-gray-600">Loading profile...</p>
                            </div>
                        ) : selectedUser ? (
                            <div className="p-8 space-y-8">
                                {/* Basic Info */}
                                <section>
                                    <h3 className="text-lg font-semibold text-gray-800 mb-4">Basic Information</h3>
                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                        <div><span className="font-medium text-gray-600">Email:</span> <span className="text-gray-800">{selectedUser.email}</span></div>
                                        <div><span className="font-medium text-gray-600">Mobile:</span> <span className="text-gray-800">{selectedUser.mobile}</span></div>
                                        <div><span className="font-medium text-gray-600">Gender:</span> <span className="text-gray-800">{selectedUser.basicInfo.gender}</span></div>
                                        <div><span className="font-medium text-gray-600">Date of Birth:</span> <span className="text-gray-800">{selectedUser.basicInfo.dob ? new Date(selectedUser.basicInfo.dob).toLocaleDateString() : 'Not set'}</span></div>
                                        <div><span className="font-medium text-gray-600">Marital Status:</span> <span className="text-gray-800">{selectedUser.basicInfo.maritalStatus || 'Not set'}</span></div>
                                        <div><span className="font-medium text-gray-600">Subscription:</span> <span className="text-gray-800 capitalize">{selectedUser.subscriptionPlan}</span></div>
                                    </div>
                                </section>

                                {/* Location */}
                                <section>
                                    <h3 className="text-lg font-semibold text-gray-800 mb-4">Location</h3>
                                    <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                                        <div><span className="font-medium text-gray-600">City:</span> <span>{selectedUser.location.city || '—'}</span></div>
                                        <div><span className="font-medium text-gray-600">State:</span> <span>{selectedUser.location.state || '—'}</span></div>
                                        <div><span className="font-medium text-gray-600">Country:</span> <span>{selectedUser.location.country || '—'}</span></div>
                                    </div>
                                </section>

                                {/* Education & Career */}
                                <section>
                                    <h3 className="text-lg font-semibold text-gray-800 mb-4">Education & Career</h3>
                                    <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                                        <div><span className="font-medium text-gray-600">Education:</span> <span>{selectedUser.educationCareer.education || '—'}</span></div>
                                        <div><span className="font-medium text-gray-600">Occupation:</span> <span>{selectedUser.educationCareer.occupation || '—'}</span></div>
                                        <div><span className="font-medium text-gray-600">Income:</span> <span>{selectedUser.educationCareer.income || '—'}</span></div>
                                    </div>
                                </section>

                                {/* Religion & Community */}
                                <section>
                                    <h3 className="text-lg font-semibold text-gray-800 mb-4">Religion & Community</h3>
                                    <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                                        <div><span className="font-medium text-gray-600">Religion:</span> <span>{selectedUser.religionCommunity.religion || '—'}</span></div>
                                        <div><span className="font-medium text-gray-600">Caste:</span> <span>{selectedUser.religionCommunity.caste || '—'}</span></div>
                                        <div><span className="font-medium text-gray-600">Subcaste:</span> <span>{selectedUser.religionCommunity.subcaste || '—'}</span></div>
                                    </div>
                                </section>

                                {/* Horoscope */}
                                <section>
                                    <h3 className="text-lg font-semibold text-gray-800 mb-4">Horoscope Details</h3>
                                    <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                                        <div><span className="font-medium text-gray-600">Star:</span> <span>{selectedUser.horoscope.star || '—'}</span></div>
                                        <div><span className="font-medium text-gray-600">Rasi:</span> <span>{selectedUser.horoscope.rasi || '—'}</span></div>
                                        <div><span className="font-medium text-gray-600">Dosham:</span> <span>{selectedUser.horoscope.dosham || '—'}</span></div>
                                    </div>
                                </section>

                                {/* Lifestyle */}
                                <section>
                                    <h3 className="text-lg font-semibold text-gray-800 mb-4">Lifestyle</h3>
                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                        <div><span className="font-medium text-gray-600">Diet:</span> <span>{selectedUser.lifestyle.diet || '—'}</span></div>
                                        <div><span className="font-medium text-gray-600">Drinking:</span> <span>{selectedUser.lifestyle.drinking || '—'}</span></div>
                                        <div><span className="font-medium text-gray-600">Smoking:</span> <span>{selectedUser.lifestyle.smoking || '—'}</span></div>
                                    </div>
                                </section>

                                {/* Photos */}
                                {(selectedUser.profilePhoto || selectedUser.photos?.length > 0) && (
                                    <section>
                                        <h3 className="text-lg font-semibold text-gray-800 mb-4">Photos</h3>
                                        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                                            {selectedUser.profilePhoto && (
                                                <img src={selectedUser.profilePhoto} alt="Profile" className="w-full h-48 object-cover rounded-xl shadow-md" />
                                            )}
                                            {selectedUser.photos?.map((photo, i) => (
                                                <img key={i} src={photo} alt={`Photo ${i + 1}`} className="w-full h-48 object-cover rounded-xl shadow-md" />
                                            ))}
                                        </div>
                                    </section>
                                )}

                                {selectedUser.jathagamPhoto && (
                                    <section>
                                        <h3 className="text-lg font-semibold text-gray-800 mb-4">Jathagam</h3>
                                        <img src={selectedUser.jathagamPhoto} alt="Jathagam" className="w-full max-w-2xl rounded-xl shadow-lg" />
                                    </section>
                                )}
                            </div>
                        ) : (
                            <div className="p-16 text-center text-gray-500">No profile data available</div>
                        )}
                    </div>
                </div>
            )}

            {/* Confirmation Modal */}
            {showConfirmModal && pendingAction && (
                <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4">
                    <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md">
                        <div className="px-8 py-6 border-b border-gray-200">
                            <h3 className="text-xl font-bold text-gray-800">{getActionTitle(pendingAction.action)}</h3>
                        </div>
                        <div className="px-8 py-6">
                            <p className="text-gray-700">
                                {getActionMessage(pendingAction.action, pendingAction.userName)}
                            </p>
                            {pendingAction.action === 'delete' && (
                                <p className="text-red-600 text-sm mt-3 font-medium">
                                    Warning: This action is irreversible.
                                </p>
                            )}
                        </div>
                        <div className="px-8 py-5 border-t border-gray-200 flex justify-end space-x-3">
                            <button
                                onClick={() => {
                                    setShowConfirmModal(false);
                                    setPendingAction(null);
                                }}
                                className="px-6 py-3 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-xl font-medium transition-colors duration-200"
                            >
                                Cancel
                            </button>
                            <button
                                onClick={executeAction}
                                className={`px-6 py-3 rounded-xl font-medium transition-all duration-200 shadow-lg ${pendingAction.action === 'delete'
                                    ? 'bg-red-500 hover:bg-red-600 text-white shadow-red-500/30'
                                    : 'bg-blue-500 hover:bg-blue-600 text-white shadow-blue-500/30'
                                    }`}
                            >
                                Confirm
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Add User Modal */}
            {showAddModal && (
                <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4 animate-fade-in">
                    <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg max-h-[90vh] overflow-y-auto">
                        {/* Modal Header */}
                        <div className="sticky top-0 bg-white border-b border-gray-200 px-8 py-6 rounded-t-2xl">
                            <div className="flex justify-between items-center">
                                <div>
                                    <h2 className="text-2xl font-bold text-gray-800">Add New User</h2>
                                    <p className="text-gray-600 mt-1">Create a new user account</p>
                                </div>
                                <button
                                    onClick={() => setShowAddModal(false)}
                                    className="p-2.5 hover:bg-gray-100 rounded-xl transition-colors duration-200"
                                >
                                    <FaTimes className="text-gray-500 text-xl" />
                                </button>
                            </div>
                        </div>

                        {/* Modal Body */}
                        <form onSubmit={handleAddUser} className="px-8 py-6 space-y-5">
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                                {/* Name */}
                                <div>
                                    <label className="block text-sm font-semibold text-gray-700 mb-2">Full Name</label>
                                    <div className="relative">
                                        <FaUserCircle className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
                                        <input
                                            required
                                            type="text"
                                            value={newUser.name}
                                            onChange={e => setNewUser({ ...newUser, name: e.target.value })}
                                            className="w-full pl-10 pr-4 py-3 bg-gray-50 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-gray-700"
                                            placeholder="John Doe"
                                        />
                                    </div>
                                </div>

                                {/* Email */}
                                <div>
                                    <label className="block text-sm font-semibold text-gray-700 mb-2">Email Address</label>
                                    <input
                                        required
                                        type="email"
                                        value={newUser.email}
                                        onChange={e => setNewUser({ ...newUser, email: e.target.value })}
                                        className="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-gray-700"
                                        placeholder="john@example.com"
                                    />
                                </div>

                                {/* Mobile */}
                                <div>
                                    <label className="block text-sm font-semibold text-gray-700 mb-2">Mobile Number</label>
                                    <div className="relative">
                                        <FaPhone className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
                                        <input
                                            required
                                            type="text"
                                            value={newUser.mobile}
                                            onChange={e => setNewUser({ ...newUser, mobile: e.target.value })}
                                            className="w-full pl-10 pr-4 py-3 bg-gray-50 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-gray-700"
                                            placeholder="+1 234 567 8900"
                                        />
                                    </div>
                                </div>

                                {/* Gender */}
                                <div>
                                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                                        Gender
                                    </label>

                                    <CustomDropdown
                                        value={newUser.gender}
                                        onChange={(val) => setNewUser({ ...newUser, gender: val })}
                                        options={genderOptions}
                                        iconMap={genderIcons}
                                        placeholder="Select gender"
                                    />
                                </div>


                                {/* Password */}
                                <div className="md:col-span-2">
                                    <label className="block text-sm font-semibold text-gray-700 mb-2">Password</label>
                                    <div className="relative">
                                        <FaKey className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
                                        <input
                                            required
                                            type="password"
                                            value={newUser.password}
                                            onChange={e => setNewUser({ ...newUser, password: e.target.value })}
                                            className="w-full pl-10 pr-4 py-3 bg-gray-50 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-gray-700"
                                            placeholder="Enter secure password"
                                        />
                                    </div>
                                </div>

                                {/* Role */}
                                <div className="md:col-span-2">
                                    <label className="block text-sm font-semibold text-gray-700 mb-2">Role</label>
                                    <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                                        {['user', 'admin', 'broker'].map((role) => (
                                            <button
                                                key={role}
                                                type="button"
                                                onClick={() => setNewUser({ ...newUser, role })}
                                                className={`px-4 py-3 rounded-xl border-2 transition-all duration-200 ${newUser.role === role
                                                    ? 'border-blue-500 bg-blue-50 text-blue-600'
                                                    : 'border-gray-200 bg-white text-gray-700 hover:border-gray-300'
                                                    }`}
                                            >
                                                <div className="flex items-center justify-center space-x-2">
                                                    {getRoleIcon(role)}
                                                    <span className="font-medium capitalize">{role}</span>
                                                </div>
                                            </button>
                                        ))}
                                    </div>
                                </div>
                            </div>

                            {/* Modal Footer */}
                            <div className="pt-6 border-t border-gray-200">
                                <div className="flex justify-end space-x-3">
                                    <button
                                        type="button"
                                        onClick={() => setShowAddModal(false)}
                                        className="px-6 py-3 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-xl font-medium transition-colors duration-200"
                                    >
                                        Cancel
                                    </button>
                                    <button
                                        type="submit"
                                        className="px-6 py-3 bg-blue-500 hover:bg-blue-600 text-white rounded-xl font-medium shadow-lg shadow-blue-500/30 transition-all duration-200"
                                    >
                                        Create User Account
                                    </button>
                                </div>
                            </div>
                        </form>
                    </div>
                </div>
            )}
        </div>
    );
};

export default UserManagement;